import {
  getJobIdFromStoredJwt,
  getJobYearFromStoredJwt,
  getStoredJwtExpiryMs,
  getUserIdFromStoredJwt,
} from '@/api/services/auth.service'
import { getStoredConnectionId } from '@/providers/websocket/websocket-context'
import { getFormattedVersion } from '@/utils/version'

export interface SessionInfo {
  build: string
  jobId: string
  jobYear: string
  userId: string
  connectionId: string
  jwtExpiresIn: string
}

/** Matches PageViewMode from home.tsx without importing it (avoids coupling). */
export type SessionPageViewMode = 'spread' | 'single-first' | 'single-last' | 'endsheet' | 'cover'

export interface SpreadContext {
  spreadId?: string
  /** 1-based spread index */
  currentSpread?: number
  totalSpreads?: number
  pageViewMode?: SessionPageViewMode
}

const LABEL_WIDTH = 12

function orDash(value: string | null | undefined): string {
  if (value == null || value === '') return '—'

  return value
}

export function formatJwtExpiry(expiryMs: number | null, now: number = Date.now()): string {
  if (expiryMs == null) return '—'

  const remaining = expiryMs - now

  if (remaining <= 0) return 'expired'

  if (remaining < 60_000) {
    return `${Math.ceil(remaining / 1000)}s`
  }

  return `${Math.ceil(remaining / 60_000)}m`
}

function spreadModeSuffix(mode?: SessionPageViewMode): string {
  if (!mode) return ''

  if (mode === 'spread') return ' · double spread'
  if (mode === 'single-first' || mode === 'single-last') return ' · single spread'
  if (mode === 'endsheet') return ' · endsheet'
  if (mode === 'cover') return ' · cover'

  return ''
}

export function formatSpread(ctx: SpreadContext): string {
  const { currentSpread, totalSpreads, pageViewMode } = ctx

  if (currentSpread == null || totalSpreads == null || totalSpreads < 1) {
    return '—'
  }

  const padded = String(currentSpread).padStart(4, '0')

  return `${padded} of ${totalSpreads}${spreadModeSuffix(pageViewMode)}`
}

function formatLabelValue(label: string, value: string): string {
  return `${label.padEnd(LABEL_WIDTH)}${value}`
}

export function getSessionInfo(now: number = Date.now()): SessionInfo {
  const jobYear = getJobYearFromStoredJwt()

  return {
    build: getFormattedVersion(),
    jobId: orDash(getJobIdFromStoredJwt()),
    jobYear: jobYear != null ? String(jobYear) : '—',
    userId: orDash(getUserIdFromStoredJwt()),
    connectionId: orDash(getStoredConnectionId()),
    jwtExpiresIn: formatJwtExpiry(getStoredJwtExpiryMs(), now),
  }
}

export function formatSessionLines(session: SessionInfo, ctx: SpreadContext): string {
  return [
    formatLabelValue('Build', session.build),
    formatLabelValue('Job ID', session.jobId),
    formatLabelValue('Job Year', session.jobYear),
    formatLabelValue('Spread ID', orDash(ctx.spreadId)),
    formatLabelValue('User ID', session.userId),
    formatLabelValue('Connection', session.connectionId),
    formatLabelValue('Checkpoint', '—'),
    formatLabelValue('Book', '—'),
    formatLabelValue('Spread', formatSpread(ctx)),
    formatLabelValue('JWT expires', session.jwtExpiresIn),
  ].join('\n')
}
